import type { Shift, ShiftPayload } from "@ots-frontend/ots-data";

/**
 * What a new or edited shift starts out with, e.g. built from selected driver activity. Without an `id` it is
 * a new shift.
 */
export type ShiftDraft = {
  id?: string;
  regionId?: string;
  personId?: string;
  vehicleId?: string;
  trailerId?: string;
  shiftTypeId?: string;
  startDttm?: number;
  endDttm?: number;
  preDttm?: number;
  postDttm?: number;
  /** Minutes billed before the shift started and after it ended, as WaterGo's pre and post billable shift time. */
  preBillableMinutes?: number;
  postBillableMinutes?: number;
  startOdometer?: number;
  endOdometer?: number;
  odometerUom?: string;
};

/** What the shift form edits. Times are epoch milliseconds. */
export type ShiftFormValues = {
  personId?: string;
  vehicleId?: string;
  trailerId?: string;
  startDttm?: number;
  endDttm?: number;
  /** When the pre-trip and post-trip inspections were done. */
  preDttm?: number;
  postDttm?: number;
  preBillableMinutes?: number;
  postBillableMinutes?: number;
  startOdometer?: number;
  endOdometer?: number;
  odometerUom?: string;
  comment?: string;
};

/** Names of the driver and equipment chosen, so the saved shift reads right before the server's next summary. */
export type ShiftFormNames = {
  personName?: string;
  vehicleName?: string;
  trailerName?: string;
};

export type ShiftFormErrors = Partial<Record<keyof ShiftFormValues, string>>;

/** What has to hold before a shift can be saved; the server checks overlaps with the driver's other shifts. */
export const validateShiftForm = (values: ShiftFormValues): ShiftFormErrors => {
  const errors: ShiftFormErrors = {};
  if (!values.personId) errors.personId = "Choose the driver.";
  if (!values.trailerId) errors.trailerId = "Choose the trailer.";
  if (values.startDttm == null) errors.startDttm = "Enter when the shift started.";
  if (values.startDttm != null && values.endDttm != null && values.endDttm < values.startDttm) {
    errors.endDttm = "The shift has to end after it starts.";
  }
  if (values.preDttm != null && values.startDttm != null && values.preDttm > values.startDttm) {
    errors.preDttm = "The pre-trip inspection comes before the shift starts.";
  }
  if (values.postDttm != null && values.endDttm == null) {
    errors.postDttm = "Enter when the shift ended before its post-trip inspection.";
  } else if (values.postDttm != null && values.endDttm != null && values.postDttm < values.endDttm) {
    errors.postDttm = "The post-trip inspection comes after the shift ends.";
  }
  for (const field of ["preBillableMinutes", "postBillableMinutes"] as const) {
    const minutes = values[field];
    if (minutes != null && (minutes < 0 || !Number.isInteger(minutes))) errors[field] = "Enter whole minutes.";
  }
  if (values.startOdometer != null && values.endOdometer != null && values.endOdometer < values.startOdometer) {
    errors.endOdometer = "The end reading can't be below the start.";
  }
  return errors;
};

const withoutUndefined = <T extends object>(value: T): T =>
  Object.fromEntries(Object.entries(value).filter(([, field]) => field !== undefined)) as T;

/** Edits a shift as it is. */
export const shiftDraftFromShift = (shift: Shift): ShiftDraft => withoutUndefined({
  id: shift.id,
  regionId: shift.regionId,
  personId: shift.personId,
  vehicleId: shift.vehicleId,
  trailerId: shift.trailerId,
  shiftTypeId: shift.shiftTypeId,
  startDttm: shift.startDttm,
  endDttm: shift.endDttm,
  preDttm: shift.preDttm,
  postDttm: shift.postDttm,
  preBillableMinutes: shift.preBillableDttm,
  postBillableMinutes: shift.postBillableDttm,
  startOdometer: shift.startOdometer,
  endOdometer: shift.endOdometer,
  odometerUom: shift.odometerUom,
});

/**
 * The driver's next shift, as WaterGo's "New Shift" from a shift: same driver, equipment, and type, starting
 * where this one ended, with the odometer picking up from its last reading.
 */
export const nextShiftDraft = (shift: Shift): ShiftDraft => {
  const start = shift.endDttm ?? shift.startDttm;
  return withoutUndefined({
    regionId: shift.regionId,
    personId: shift.personId,
    vehicleId: shift.vehicleId,
    trailerId: shift.trailerId,
    shiftTypeId: shift.shiftTypeId,
    startDttm: start,
    startOdometer: shift.endOdometer ?? shift.startOdometer,
    odometerUom: shift.odometerUom,
  });
};

const SERVER_OWNED = new Set(["messages", "startGeofenceEvent", "endGeofenceEvent"]);

/**
 * The save request: an edit is the stored shift with the form's fields over it, a new shift is the form's
 * fields in the given region. A comment goes along as a new message.
 */
export const toShiftPayload = (
  values: ShiftFormValues,
  {stored, regionId, shiftTypeId, names, now = Date.now()}: {stored?: Shift; regionId?: string; shiftTypeId?: string; names?: ShiftFormNames; now?: number},
): ShiftPayload => {
  const comment = values.comment?.trim();
  const fields = {
    personId: values.personId,
    vehicleId: values.vehicleId,
    trailerId: values.trailerId,
    startDttm: values.startDttm,
    endDttm: values.endDttm,
    preDttm: values.preDttm,
    postDttm: values.postDttm,
    // As WaterGo saves them: none rather than zero.
    preBillableDttm: values.preBillableMinutes || undefined,
    postBillableDttm: values.postBillableMinutes || undefined,
    startOdometer: values.startOdometer,
    endOdometer: values.endOdometer,
    odometerUom: values.odometerUom,
    ...names,
  };
  const messages: ShiftPayload["messages"] = comment ? [{message: comment, messageType: "COMMENT", action: "CREATE"}] : [];
  if (stored) {
    // The stored messages and matched geofence visits are the server's own; they are not sent back.
    const rest = Object.fromEntries(Object.entries(stored).filter(([key]) => !SERVER_OWNED.has(key)));
    return {...rest, ...fields, lastModifiedDttm: now, messages};
  }
  return {regionId, shiftTypeId, ...fields, messages};
};
